import { useEffect, useState, type FormEvent } from 'react';
import { Logo } from '../../components/Logo';
import { Button, Card, Field, Input, Notice } from '../../components/ui';
import { trpc, type IdentityTrpcClient } from '../../lib/trpc';
import { navigate } from '../../lib/router';
import { LanguageSelector } from '../../components/LanguageSelector';
import { useI18n } from '../../lib/i18n';

type Status = Awaited<ReturnType<IdentityTrpcClient['auth']['bootstrapStatus']['query']>>;

export function BootstrapPage({ client = trpc }: { client?: IdentityTrpcClient }) {
  const { t } = useI18n();
  const [status, setStatus] = useState<Status>();
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(new URLSearchParams(window.location.search).get('token') ?? '');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [seed, setSeed] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    client.auth.bootstrapStatus.query().then((result) => { if (active) setStatus(result); }).catch((caught) => { if (active) setError(errorMessage(caught, t('publicBootstrapStatusError'))); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, t]);

  async function requestSeed() {
    setSubmitting(true); setError('');
    try {
      const result = await client.auth.bootstrapTotpSetup.mutate({ token });
      setSeed(result.seed);
    } catch (caught) { setError(errorMessage(caught, t('publicBootstrapTokenInvalid'))); }
    finally { setSubmitting(false); }
  }

  async function complete(event: FormEvent) {
    event.preventDefault();
    if (password !== repeat) { setError(t('publicPasswordsDoNotMatch')); return; }
    if (!seed) { setError(t('publicBootstrapSeedRequired')); return; }
    setSubmitting(true); setError('');
    try {
      await client.auth.completeBootstrap.mutate({ token, email, ...(displayName ? { displayName } : {}), password, totpSeed: seed, totpCode });
      setDone(true); setSeed(''); setTotpCode('');
    } catch (caught) { setError(errorMessage(caught, t('publicBootstrapCompleteError'))); }
    finally { setSubmitting(false); }
  }

  return <main className="auth-page"><div className="auth-brand"><Logo/><LanguageSelector/></div><Card className="auth-card auth-card-wide">
    {loading ? <div className="loading-card"><span className="spinner"/><p>{t('publicBootstrapChecking')}</p></div>
      : done ? <div className="success-panel"><h1>{t('publicBootstrapConfiguredTitle')}</h1><p>{t('publicBootstrapConfiguredDescription')}</p><Button onClick={() => navigate('/')}>{t('backToLogin')}</Button></div>
      : status?.initialized || !status?.available ? <Unavailable status={status}/>
      : <><div className="auth-heading"><span className="step">{t('publicBootstrapStep')}</span><h1>{t('bootstrapTitle')}</h1><p>{t('bootstrapDescription')}</p></div>
        {error && <Notice tone="danger">{error}</Notice>}
        <form className="form-grid" onSubmit={(event) => void complete(event)}>
          <Field label={t('bootstrapToken')} required><Input value={token} onChange={(event) => { setToken(event.target.value); setSeed(''); }} minLength={20} required readOnly={Boolean(seed)}/></Field>
          <div className="field seed-action"><span className="field-label">{t('publicSecondFactor')}</span><Button type="button" variant="secondary" disabled={token.length < 20 || submitting || Boolean(seed)} onClick={() => void requestSeed()}>{seed ? t('publicTotpKeyReceived') : t('completeSetup')}</Button></div>
          {seed && <div className="full"><Field label={t('publicTotpKey')} hint={t('publicBootstrapTotpHint')}><div className="secret-box"><code tabIndex={0}>{seed}</code><CopyButton value={seed}/></div></Field></div>}
          <Field label={t('email')} required><Input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required/></Field>
          <Field label={t('publicDisplayName')}><Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={200}/></Field>
          <Field label={t('password')} required><Input type="password" autoComplete="new-password" minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} required/></Field>
          <Field label={t('repeatPassword')} required><Input type="password" autoComplete="new-password" minLength={12} value={repeat} onChange={(event) => setRepeat(event.target.value)} required/></Field>
          <Field label={t('publicSixDigitTotpCode')} required><Input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={totpCode} onChange={(event) => setTotpCode(event.target.value)} required disabled={!seed}/></Field>
          <div className="form-actions full"><Button type="submit" disabled={submitting || !seed}>{submitting ? t('loading') : t('completeSetup')}</Button></div>
        </form></>}
  </Card><p className="auth-footer">{t('protectedBy')}</p></main>;
}

function Unavailable({ status }: { status?: Status | undefined }) {
  const { t, formatDateTime } = useI18n();
  return <div className="success-panel"><h1>{status?.initialized ? t('publicBootstrapAlreadyComplete') : t('publicBootstrapUnavailable')}</h1><p>{status?.initialized ? t('publicBootstrapIdentityExists') : status?.expiresAt ? t('publicBootstrapExpired', { date: formatDateTime(status.expiresAt) }) : t('publicBootstrapServerUnavailable')}</p><Button onClick={() => navigate('/')}>{t('backToLogin')}</Button></div>;
}

function CopyButton({ value }: { value: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return <Button type="button" size="small" variant="secondary" onClick={() => void navigator.clipboard?.writeText(value).then(() => setCopied(true))}>{copied ? t('copied') : t('copy')}</Button>;
}

function errorMessage(_error: unknown, fallback: string): string {
  return fallback;
}
